import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  COLOR_ERROR_1,
  COLOR_GREY_400,
  COLOR_GREY_600,
  COLOR_PRIMARY_1_600,
  COLOR_PRIMARY_1_800,
  COLOR_PRIMARY_2,
  COLOR_PRIMARY_2_300,
  COLOR_SUCCESS_1,
  COLOR_TEXT_2,
  COLOR_TEXT_3,
  COLOR_TEXT_4,
  COLOR_TEXT_GREY,
  KPI_CARD_COLOR,
} from 'src/utils/colors';
import {
  Alert,
  Box,
  Button,
  FormControl,
  Grid,
  IconButton,
  MenuItem,
  Select,
  SelectChangeEvent,
  Skeleton,
  Typography,
} from '@mui/material';
import LockSharpIcon from '@material-ui/icons/LockSharp';
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';
import { exchangeRates, MARKET_EXPLORER_EXPORT_LIMIT_ROW_MONTHLY } from 'src/utils/const';
import {
  abbreviateNumber,
  checkUserTailorMarketAccess,
  convertCurrency,
  formatCurrency,
  getGraphExportCreditRequired,
  getMlaMonthName,
  getSeasonality,
  getSeasonalityBarColor,
  roundNumber,
  trackLast3YearsDataInterest,
} from 'src/utils/functions';
import {
  ComposedChart,
  LineChart,
  Line,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceLine,
} from 'recharts';
import { overviewPageCustomTooltipProps } from 'src/interfaces/idashboard';
import { getHistoricalData } from '../helper';
import { getOccupancyWithoutLastMinuteAssumed } from 'src/utils/occupancyConfig';
import { User } from 'src/interfaces/iuser';
import { useCannotAccessMainKPI, useForcedPlanForMainKPI } from 'src/hooks/useFeatureAccess';
import { useUpgradeMesageForMainKPI } from 'src/hooks/useUpgradeText';
import { logAmplitudeEvent } from 'src/utils/analytics';
import MainKPITooltip from 'src/App/Dashboards/Market/Components/Tooltips/MainKPITooltip';
import { InfoButton } from 'src/App/Dashboards/Market/Components/Buttons';
import { kpiTooltipConfig } from '../configs/tooltipConfig';
import { LoadingCard } from 'src/App/Dashboards/Market/Components';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useGetCreditsRemaining } from 'src/hooks/useCustomHooks';

interface DataBoxComponentProps {
  kpi: string;
  selectedMainKpi: string;
  kpiName: string;
  value: number;
  yoYChange: string;
  isLoading: boolean;
  unitType: string;
  currency: string;
}

const CustomReferenceLabel = props => {
  const { viewBox, setIsHoveringLabel, isMobileScreen } = props;
  const { x, y, width: viewBoxWidth } = viewBox || {};

  let calculatedViewboxWidth = 0;
  if (!viewBoxWidth || viewBoxWidth == 0) {
    const mainKpiGraphContainer = document.getElementById('main-kpi-graph-container');
    if (mainKpiGraphContainer) {
      calculatedViewboxWidth = mainKpiGraphContainer.clientWidth;
    }
  }

  const [isHovering, setIsHovering] = useState(false);
  const hoverTimeout = useRef<any>(null);
  const labelRef = useRef<HTMLDivElement | null>(null);
  const tooltipRef = useRef<HTMLDivElement | null>(null);

  const handleEnter = () => {
    if (hoverTimeout.current) {
      clearTimeout(hoverTimeout.current);
      hoverTimeout.current = null;
    }
    setIsHovering(true);
    setIsHoveringLabel?.(true);
  };

  const handleLeave = () => {
    hoverTimeout.current = setTimeout(() => {
      setIsHovering(false);
      hoverTimeout.current = null;
    }, 100);
    setIsHoveringLabel?.(false);
  };

  const handleTouchStart = e => {
    e.stopPropagation();
    e.preventDefault();
    handleEnter();
  };

  // Handle outside clicks/touches to close tooltip
  useEffect(() => {
    const handleClickOutside = event => {
      const label = labelRef.current;
      const tooltip = tooltipRef.current;

      if (label && tooltip && !label.contains(event.target) && !tooltip.contains(event.target)) {
        setIsHovering(false);
        setIsHoveringLabel?.(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, []);

  if (x == null || y == null) return null;

  const widthToUse = viewBoxWidth || calculatedViewboxWidth;

  // Calculate dynamic x position to prevent label truncation
  const labelWidth = 140;
  const leftMargin = 30;
  const rightMargin = 30;
  const defaultX = x - 65;
  const rightBoundary = widthToUse ? widthToUse - rightMargin : Infinity;
  const leftBoundary = leftMargin;

  const adjustedX = widthToUse
    ? Math.max(leftBoundary, Math.min(defaultX, rightBoundary - labelWidth))
    : defaultX;

  // Calculate dynamic x position for tooltip to prevent truncation
  const tooltipWidth = 320;
  const defaultTooltipX = x - 130 / 2 + 10;
  const tooltipRightBoundary = widthToUse ? widthToUse - rightMargin : Infinity;

  // Adjust tooltip x position to stay within bounds
  const adjustedTooltipX = widthToUse
    ? Math.max(leftBoundary, Math.min(defaultTooltipX, tooltipRightBoundary - tooltipWidth))
    : defaultTooltipX;

  return (
    <>
      <foreignObject
        x={adjustedX}
        y={y - 30}
        width={140}
        height={20}
        xmlns="http://www.w3.org/2000/svg"
      >
        <div
          ref={labelRef}
          onMouseEnter={handleEnter}
          onMouseLeave={handleLeave}
          onTouchStart={handleTouchStart}
          style={{
            fontSize: '12px',
            color: COLOR_TEXT_4,
            textAlign: 'center',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '2px',
            cursor: 'pointer',
            height: '100%',
            touchAction: 'manipulation',
          }}
        >
          Data Coverage Update <InfoOutlinedIcon style={{ fontSize: 14 }} />
        </div>
      </foreignObject>

      {isHovering && (
        <foreignObject
          x={isMobileScreen ? 0 : adjustedTooltipX}
          y={y - 10}
          width={320}
          height={200}
        >
          <Box
            ref={tooltipRef}
            onMouseEnter={handleEnter}
            onMouseLeave={handleLeave}
            onTouchStart={handleTouchStart}
            sx={{
              backgroundColor: '#fff',
              borderRadius: '6px',
              padding: '8px 10px',
              fontSize: '14px',
              color: COLOR_TEXT_4,
              boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.2)',
              zIndex: 999999,
              touchAction: 'manipulation',
            }}
          >
            <Typography sx={{ fontSize: '16px', fontWeight: 600, marginBottom: '6px' }}>
              Data Coverage Update
            </Typography>
            <Typography sx={{ marginBottom: '6px', fontSize: '14px' }}>
              Our scraping coverage increased significantly in August 2023, resulting in an average
              spike of 29% (ranging from 13% to 39%).
            </Typography>
            <Typography sx={{ fontSize: '14px', marginBottom: '6px' }}>
              This may cause a visible increase in supply, which does not reflect a real market
              change.
            </Typography>
          </Box>
        </foreignObject>
      )}
    </>
  );
};

const MainKPICard = ({
  constantDummyData,
  currency,
  marketLevelOccupancyRate,
  occupancyYoYChange,
  marketLevelRevenuePotential,
  marketLevelDailyRate,
  revenueYoYChange,
  dailyYoYChange,
  marketLevelActiveListings,
  marketLevelCleaningFee,
  marketLevelBookingCount,
  listingYoYChange,
  occupancyRateHistoricalData,
  occupancyPast6MonthsHistoricalData,
  revenueHistoricalData,
  revenuePast6MonthsHistoricalData,
  dailyRateHistoricalData,
  dailyRatePast6MonthsHistoricalData,
  listingHistoricalData,
  listingPast6MonthsHistoricalData,
  matches,
  handleOnSampleClick,
  search,
  selectedPercentile,
  setSelectedpercentile,
  SeasonalityChartMemoed,
  TrendsChartMemo,
  lastYearMarketTrend,
  isMarketCacheLoading,
  ComponentFilterSupportComingSoonText,
  filterCacheLoading,
  selectedMainKpi,
  setSelectedMainKpi,
  setRevenueFormattedChartData,
  mainKpiRevenueChartRef,
  seasonalityGraphData,
  trendGraphData,
  setSelectedGraphExportDetails,
}) => {
  const user: User = useSelector(state => (state as any).dashboard.user);
  const dummyDataFromSampleMarket = useSelector(state => (state as any).dashboard.lockedMarketData);
  const [dummyData, setDummyData] = useState<any>(constantDummyData);
  const [timePeriod, setTimePeriod] = useState<number>(36);
  const [seasonalityData, setSeasonalityData] = useState({});
  const [areKPIsLoading, setAreKPIsLoading] = useState(true);
  const [aug2023ReferenceMonth, setAug2023ReferenceMonth] = useState<string | null>(null);
  const [isHoveringLabel, setIsHoveringLabel] = useState(false);
  const upgradeMessage = useUpgradeMesageForMainKPI(timePeriod);
  const forcedPlan = useForcedPlanForMainKPI(timePeriod);
  const accomodationRevenueInTooltip = roundNumber(
    marketLevelDailyRate * (marketLevelOccupancyRate / 100) * 365
  );
  const cleaningFeeRevenueInTooltip = marketLevelBookingCount * marketLevelCleaningFee;
  useEffect(() => {
    if (dummyDataFromSampleMarket.data) {
      setDummyData(dummyDataFromSampleMarket.data.historical_monthly_performance);
    }
  }, [dummyDataFromSampleMarket]);

  useEffect(() => {
    if (
      isMarketCacheLoading ||
      !marketLevelOccupancyRate ||
      !marketLevelRevenuePotential ||
      !marketLevelDailyRate ||
      !marketLevelActiveListings
    ) {
      setAreKPIsLoading(true);
    } else {
      setAreKPIsLoading(false);
    }
  }, [
    marketLevelOccupancyRate,
    marketLevelRevenuePotential,
    marketLevelDailyRate,
    marketLevelActiveListings,
    isMarketCacheLoading,
  ]);

  let userTailorMarketAccess = checkUserTailorMarketAccess(user, search);

  const isAccessDenied = useCannotAccessMainKPI(timePeriod);

  const GRAPH_DATA_MAPPING = {
    occupancy: {
      36: occupancyRateHistoricalData,
      24: occupancyRateHistoricalData,
      12: occupancyRateHistoricalData,
      6: occupancyPast6MonthsHistoricalData,
    },
    revenue: {
      36: revenueHistoricalData,
      24: revenueHistoricalData,
      12: revenueHistoricalData,
      6: revenuePast6MonthsHistoricalData,
    },
    daily: {
      36: dailyRateHistoricalData,
      24: dailyRateHistoricalData,
      12: dailyRateHistoricalData,
      6: dailyRatePast6MonthsHistoricalData,
    },
    listing: {
      36: listingHistoricalData,
      24: listingHistoricalData,
      12: listingHistoricalData,
      6: listingPast6MonthsHistoricalData,
    },
  };

  const mainKpiOccupancyDummyData = getHistoricalData(dummyData, 'occupancy')[0];
  const mainKpiRevenueDummyData = getHistoricalData(dummyData, 'revenue')[0];
  const mainKpiListingDummydata = getHistoricalData(dummyData, 'listing')[0];
  const mainKpiDailyRateDummydata = getHistoricalData(dummyData, 'daily')[0];

  const GRAPH_DUMMY_DATA_MAPPING = {
    occupancy: {
      36: mainKpiOccupancyDummyData,
      24: mainKpiOccupancyDummyData,
      12: mainKpiOccupancyDummyData,
      6: mainKpiOccupancyDummyData.slice(-6),
    },
    revenue: {
      36: mainKpiRevenueDummyData,
      24: mainKpiRevenueDummyData,
      12: mainKpiRevenueDummyData,
      6: mainKpiRevenueDummyData.slice(-6),
    },
    daily: {
      36: mainKpiDailyRateDummydata,
      24: mainKpiDailyRateDummydata,
      12: mainKpiDailyRateDummydata,
      6: mainKpiDailyRateDummydata.slice(-6),
    },
    listing: {
      36: mainKpiListingDummydata,
      24: mainKpiListingDummydata,
      12: mainKpiListingDummydata,
      6: mainKpiListingDummydata.slice(-6),
    },
  };

  const GRAPH_LINE_DATA_MAPPING = {
    occupancy: {
      25: 'occupancy_rate25',
      50: 'occupancy_rate50',
      75: 'occupancy_rate75',
      90: 'occupancy_rate90',
    },
    revenue: { 25: 'revenue25', 50: 'revenue50', 75: 'revenue75', 90: 'revenue90' },
    daily: { 25: 'daily_rate25', 50: 'daily_rate50', 75: 'daily_rate75', 90: 'daily_rate90' },
    listing: {
      25: 'active_listings_count',
      50: 'active_listings_count',
      75: 'active_listings_count',
      90: 'active_listings_count',
    },
  };

  useEffect(() => {
    let dataUsedToCalculateSeasonality = isAccessDenied
      ? GRAPH_DUMMY_DATA_MAPPING['revenue'][timePeriod]
      : GRAPH_DATA_MAPPING['revenue'][timePeriod];
    let seasonilityData = getSeasonality(
      dataUsedToCalculateSeasonality,
      'overview',
      selectedPercentile
    );
    setSeasonalityData(seasonilityData);
  }, [
    timePeriod,
    revenueHistoricalData,
    revenuePast6MonthsHistoricalData,
    selectedPercentile,
    isAccessDenied,
  ]);

  useEffect(() => {
    // Example usage of formatRevenueChartDataForPDF function
    try {
      const revenueData = GRAPH_DATA_MAPPING['revenue'][timePeriod];
      const formattedData = formatRevenueChartDataForPDF(revenueData, selectedPercentile);
      setRevenueFormattedChartData(formattedData);
    } catch (e) {
      setRevenueFormattedChartData(null);
    }
  }, [
    selectedMainKpi,
    timePeriod,
    selectedPercentile,
    isAccessDenied,
    revenueHistoricalData,
    currency,
  ]);

  useEffect(() => {
    if (user.subscriptionPlan === 'pro') {
      setTimePeriod(24);
    }
  }, [user.subscriptionPlan]);

  useEffect(() => {
    try {
      const data = isAccessDenied
        ? GRAPH_DUMMY_DATA_MAPPING[selectedMainKpi][timePeriod]
        : GRAPH_DATA_MAPPING[selectedMainKpi][timePeriod];

      const aug2023Entry = data.find(item => {
        return item.month && item.month === 'Aug' && item['2023'];
      });

      setAug2023ReferenceMonth(aug2023Entry?.month || null);
    } catch (e) {
      setAug2023ReferenceMonth(null);
    }
  }, [GRAPH_DATA_MAPPING, isAccessDenied, selectedMainKpi, timePeriod]);

  const handleSelectedKpiChange = (kpi: string) => {
    setSelectedMainKpi(kpi);
    logAmplitudeEvent('main_kpi_metric_switched');
  };

  const handleTimePeriodChange = (event: SelectChangeEvent) => {
    const value = parseInt(event.target.value);
    setTimePeriod(value);
    if (value == 36) trackLast3YearsDataInterest();
    logAmplitudeEvent('main_kpi_metric_configured');
  };

  const combineFormatterYAxis = (val: number) => {
    if (selectedMainKpi.includes('revenue') || selectedMainKpi.includes('daily')) {
      if (val > 1000) {
        return (
          `${currency && exchangeRates[currency].symbol}` +
          abbreviateNumber(convertCurrency(currency, val))
        );
      } else {
        return (
          `${currency && exchangeRates[currency].symbol}` +
          abbreviateNumber(convertCurrency(currency, val), false)
        );
      }
    } else if (selectedMainKpi.includes('occupancy')) {
      return val + '%';
    } else {
      return abbreviateNumber(val, false);
    }
  };

  const monthTickFormattor = (val: string) => {
    return getMlaMonthName(val);
  };

  const KPIDataLoading = ({}) => {
    return <Skeleton variant="text" sx={{ width: '70%' }} />;
  };

  const getDataToExport = () => {
    let parsedGraphData: any = [];

    if (['seasonality', 'trend'].includes(selectedMainKpi)) {
      const graphData = selectedMainKpi === 'seasonality' ? seasonalityGraphData : trendGraphData;
      for (const item of graphData) {
        parsedGraphData.push({
          [item.key]: item.value,
        });
      }
      return parsedGraphData;
    } else {
      const graphData = GRAPH_DATA_MAPPING[selectedMainKpi][timePeriod];

      for (const item of graphData) {
        const kpiKey = GRAPH_LINE_DATA_MAPPING[selectedMainKpi][selectedPercentile];

        const currentYearValue = item[item.latestYear][kpiKey]
          ? {
              [item.month + '_' + item.latestYear]: item[item.latestYear][kpiKey],
            }
          : null;

        const previousYearValue = item[item.previousYear][kpiKey]
          ? {
              [item.month + '_' + item.previousYear]: item[item.previousYear][kpiKey],
            }
          : null;

        const twoYearsAgoValue =
          timePeriod == 36 && item[item.twoYearsBefore][kpiKey]
            ? {
                [item.month + '_' + item.twoYearsBefore]: item[item.twoYearsBefore][kpiKey],
              }
            : null;

        currentYearValue && parsedGraphData.push(currentYearValue);
        previousYearValue && parsedGraphData.push(previousYearValue);
        twoYearsAgoValue && parsedGraphData.push(twoYearsAgoValue);
      }

      // need to sort the parsedGraphData by month year
      parsedGraphData.sort((a: any, b: any) => {
        const aMonth = Object.keys(a)[0].split('_')[0];
        const bMonth = Object.keys(b)[0].split('_')[0];
        const aYear = Object.keys(a)[0].split('_')[1];
        const bYear = Object.keys(b)[0].split('_')[1];
        return aYear.localeCompare(bYear) || aMonth.localeCompare(bMonth);
      });
      parsedGraphData = parsedGraphData.reverse();

      return parsedGraphData;
    }
  };

  const handleDownloadGraph = () => {
    const graphData = getDataToExport();
    const creditRequiredToExport = graphData.length;

    let graphDataInExportCSVFormat = graphData.map(item => [
      Object.keys(item)[0].replace('_', ' '),
      item[Object.keys(item)[0]],
    ]);
    graphDataInExportCSVFormat.unshift(['Date', `Value (${selectedMainKpi})`]);

    setSelectedGraphExportDetails({
      graphData: graphDataInExportCSVFormat,
      fieldName: 'kpi_graph.csv',
      graphType: 'mainKpiGraphs',
    });
  };

  const CustomLegend = props => {
    const { payload } = props;
    return (
      <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
        <Box sx={{ display: 'flex', gap: 1, marginRight: '10px' }}>
          <Box
            sx={{ borderTop: '4px solid #037A90', width: '15px', position: 'relative', top: '5px' }}
          />
          <Typography sx={{ fontSize: '10px' }}>Last 12 Months </Typography>
        </Box>

        {timePeriod == 24 && (
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Box
              sx={{
                borderTop: '2px dashed #4FA2B1',
                width: '15px',
                position: 'relative',
                top: '5px',
              }}
            />
            <Typography sx={{ fontSize: '10px' }}>Preceding Period</Typography>
          </Box>
        )}

        {timePeriod == 36 && (
          <Box sx={{ display: 'flex', gap: 1, marginRight: '10px' }}>
            <Box
              sx={{
                borderTop: `2px dashed ${COLOR_PRIMARY_1_800}`,
                width: '15px',
                position: 'relative',
                top: '5px',
              }}
            />
            <Typography sx={{ fontSize: '10px' }}>Previous Year</Typography>
          </Box>
        )}

        {timePeriod == 36 && (
          <Box sx={{ display: 'flex', gap: 1, marginRight: '10px' }}>
            <Box
              sx={{
                borderTop: `2px dashed ${COLOR_PRIMARY_2_300}`,
                width: '15px',
                position: 'relative',
                top: '5px',
              }}
            />
            <Typography sx={{ fontSize: '10px' }}>2 Years Ago</Typography>
          </Box>
        )}
      </Box>
    );
  };

  const CustomTooltip: React.FC<overviewPageCustomTooltipProps> = ({
    active,
    payload,
    label,
    isHoveringLabel,
  }) => {
    useEffect(() => {
      if (active) {
        logAmplitudeEvent('tooltip_interacted', {
          tooltip_type: 'graph',
          parent: `main_kpi_${selectedMainKpi}`,
        });
      }
    }, [active]);

    if (active && payload && payload.length) {
      let kpiName = '';

      if (selectedMainKpi == 'revenue') {
        kpiName = 'Revenue';
      } else if (selectedMainKpi == 'occupancy') {
        kpiName = 'Occupancy Rate';
      } else if (selectedMainKpi == 'daily') {
        kpiName = 'Nightly Rate';
      } else {
        kpiName = 'Active Listing Count';
      }

      const currentMonth = getMlaMonthName(payload[0]['payload'].month);

      if (isHoveringLabel) return null;

      return (
        <Box
          sx={{
            maxWidth: '300px',
            backgroundColor: '#ffff',
            borderRadius: '10px',
            boxShadow: '0 4px 10px #0003',
            display: 'flex',
            flexDirection: 'column',
            padding: '10px',
            paddingRight: '30px',
          }}
        >
          <Typography variant="body1" style={{ fontWeight: 'bold', color: COLOR_TEXT_2 }}>
            {selectedPercentile == 50 ? 'Median' : 100 - Number(selectedPercentile) + 'th'}{' '}
            {kpiName}
            <br />
            {currentMonth}
          </Typography>
          {[0, 1, 2].map(index => {
            const dataPoint = (payload[0] as { payload?: Record<string, unknown> } | undefined)
              ?.payload as Record<string, unknown> | undefined;
            const yearKey =
              index === 0 ? 'latestYear' : index === 1 ? 'previousYear' : 'twoYearsBefore';
            const yearLabel = dataPoint?.[yearKey];
            const occupancyDataKey =
              selectedMainKpi === 'occupancy'
                ? GRAPH_LINE_DATA_MAPPING.occupancy[selectedPercentile]
                : null;

            let valueDisplay: string;
            if (kpiName === 'Occupancy Rate' && occupancyDataKey && dataPoint) {
              const high =
                index === 0
                  ? dataPoint.LTMData?.[occupancyDataKey]
                  : index === 1
                    ? dataPoint.last2YearsData?.[occupancyDataKey]
                    : dataPoint.last3YearsData?.[occupancyDataKey];
              const low = high != null && !isNaN(high) ? getOccupancyWithoutLastMinuteAssumed(Number(high)) : null;
              valueDisplay =
                low != null && high != null ? `${low}% – ${high}%` : '-';
            } else if (payload[index]) {
              valueDisplay =
                kpiName !== 'Occupancy Rate' && kpiName !== 'Active Listing Count'
                  ? `${currency && exchangeRates[currency].symbol}`
                  : '';
              valueDisplay += formatCurrency(
                kpiName !== 'Occupancy Rate' && kpiName !== 'Active Listing Count'
                  ? convertCurrency(currency, payload[index].value).toString()
                  : payload[index].value.toString()
              );
              if (kpiName === 'Occupancy Rate') valueDisplay += '%';
            } else {
              valueDisplay = '-';
            }

            if (!payload[index] && !(kpiName === 'Occupancy Rate' && dataPoint)) return null;

            return (
              <Typography
                key={index}
                variant="body1"
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-start',
                  color: index === 0 ? COLOR_PRIMARY_2 : COLOR_GREY_600,
                  fontWeight: index === 0 ? 800 : 500,
                  gap: '5px',
                }}
              >
                <span>{yearLabel}:</span>
                <span>{valueDisplay}</span>
              </Typography>
            );
          })}
        </Box>
      );
    }

    return null;
  };

  const isGraphDataPresent: boolean =
    selectedMainKpi == 'seasonality' || selectedMainKpi === 'trend'
      ? false
      : isAccessDenied
      ? GRAPH_DUMMY_DATA_MAPPING[selectedMainKpi][timePeriod].length > 0
      : GRAPH_DATA_MAPPING[selectedMainKpi][timePeriod].length > 0;

  const revenueTooltipConfig = kpiTooltipConfig['revenue'];

  const isTrendOrSeasonality = selectedMainKpi === 'trend' || selectedMainKpi === 'seasonality';

  const showDataCoverageUpdateSignalReferenceLine =
    isGraphDataPresent && aug2023ReferenceMonth && selectedMainKpi === 'listing' && !isAccessDenied;

  const occupancyChartDataWithBand = React.useMemo(() => {
    if (selectedMainKpi !== 'occupancy') return null;
    const rawData = isAccessDenied
      ? GRAPH_DUMMY_DATA_MAPPING.occupancy[timePeriod]
      : GRAPH_DATA_MAPPING.occupancy[timePeriod];
    if (!rawData || !Array.isArray(rawData)) return rawData;
    const dataKey = GRAPH_LINE_DATA_MAPPING.occupancy[selectedPercentile];
    return rawData.map(item => {
      const occupancy = item.LTMData?.[dataKey];
      const confirmed =
        occupancy != null && !isNaN(occupancy) ? getOccupancyWithoutLastMinuteAssumed(occupancy) : 0;
      const band = occupancy != null && !isNaN(occupancy) ? Math.max(0, occupancy - confirmed) : 0;
      return {
        ...item,
        occupancy_confirmed_low: confirmed,
        occupancy_band: band,
      };
    });
  }, [
    selectedMainKpi,
    timePeriod,
    selectedPercentile,
    isAccessDenied,
    occupancyRateHistoricalData,
    occupancyPast6MonthsHistoricalData,
    mainKpiOccupancyDummyData,
  ]);

  // Function to format revenue chart data for PDF export
  const formatRevenueChartDataForPDF = (revenueData, selectedPercentile = 50) => {
    if (!revenueData || !Array.isArray(revenueData)) {
      return {
        ltm: [],
        ptm: [],
      };
    }

    // Get the data key for the selected percentile
    const dataKey = GRAPH_LINE_DATA_MAPPING.revenue[selectedPercentile];

    // Format LTM data (last 12 months)
    const ltm = revenueData.map(item => ({
      label: item.month,
      value: Math.round(item.LTMData?.[dataKey] || 0),
    }));

    // Format PTM data (past 12 months before LTM)
    const ptm = revenueData.map(item => ({
      time_period: item.month,
      value: Math.round(item.last2YearsData?.[dataKey] || 0),
    }));

    return {
      ltm,
      ptm,
    };
  };

  const DataBoxComponent = ({
    kpi,
    selectedMainKpi,
    kpiName,
    value,
    yoYChange,
    isLoading,
    unitType,
    currency,
  }: DataBoxComponentProps) => {
    return (
      <Box
        sx={{
          height: '100px',
          width: 'auto',
          ':hover': {
            cursor: 'pointer',
          },
        }}
        onClick={() => {
          handleSelectedKpiChange(kpi);
        }}
      >
        {selectedMainKpi === kpi && (
          <Box
            sx={{
              height: '4%',
              backgroundColor: KPI_CARD_COLOR,
            }}
          ></Box>
        )}
        <Grid item container xs={12}>
          <Grid
            item
            xs={12}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '3px',
              color: '#000',
              padding: matches ? '3px' : '3px 10px',
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: '500', fontSize: '12px' }}>
              {kpiName}
            </Typography>
            <InfoButton
              tooltipContent={
                <MainKPITooltip tooltipConfig={kpiTooltipConfig[kpi]} matches={matches} />
              }
            />
          </Grid>
          <Grid
            item
            xs={12}
            sx={{
              color: '#000',
              padding: matches ? '3px' : '3px 10px',
            }}
          >
            <Typography
              variant="h6"
              className="notranslate"
              sx={{
                fontWeight: selectedMainKpi === kpi ? 600 : 400,
                fontSize: matches ? '30px' : '32px',
                color:
                  selectedMainKpi === kpi
                    ? kpi === 'seasonality'
                      ? getSeasonalityBarColor(value, false)
                      : KPI_CARD_COLOR
                    : COLOR_TEXT_3,
              }}
            >
              {' '}
              {isLoading ? (
                <KPIDataLoading />
              ) : (
                <>
                  {unitType === '$' && exchangeRates[currency].symbol}
                  {unitType === '$'
                    ? formatCurrency(
                        String(
                          matches
                            ? abbreviateNumber(convertCurrency(currency, value))
                            : convertCurrency(currency, value)
                        )
                      )
                    : kpi === 'seasonality'
                    ? getSeasonalityBarColor(value, true)
                    : Number.isInteger(value)
                    ? abbreviateNumber(value)
                    : value}
                  {unitType === '%' && '%'}
                </>
              )}
            </Typography>
          </Grid>
        </Grid>
        {selectedMainKpi === kpi && (
          <Box
            sx={{
              height: '28%',
              width: '100%',
              color: '#000',
              textAlign: 'end',
              position: 'relative',
              top: '-5px',
              right: '5px',
            }}
          >
            <Typography
              variant="body2"
              sx={{
                color: yoYChange && Number(yoYChange) > 0 ? COLOR_SUCCESS_1 : COLOR_ERROR_1,
                fontSize: '10px',
              }}
            >
              {matches ? '' : 'YoY Change:'}{' '}
              {yoYChange ? yoYChange : <LockSharpIcon style={{ fontSize: 20, color: 'inherit' }} />}
              {yoYChange != 'N/A' ? '%' : ''}{' '}
            </Typography>
          </Box>
        )}
      </Box>
    );
  };

  return (
    <Box sx={{ height: '100%', width: '100%', pb: '20px' }}>
      <Box
        sx={
          matches
            ? {
                height: '120px',
                width: '100%',
                padding: '10px',
                display: 'grid',
                gridAutoFlow: 'column',
                gridAutoColumns: matches ? 'minmax(120px, 1fr)' : 'minmax(200px, 1fr)',
                overflowX: 'auto',
                overflowY: 'hidden',
              }
            : {
                width: '97%',
                padding: '10px',
                display: 'grid',
                gridTemplateColumns: matches ? 'repeat(3, 1fr)' : 'repeat(6, 1fr)',
              }
        }
      >
        {/* revenue Box */}
        <DataBoxComponent
          kpi="revenue"
          selectedMainKpi={selectedMainKpi}
          kpiName="Revenue"
          value={marketLevelRevenuePotential}
          yoYChange={revenueYoYChange}
          isLoading={areKPIsLoading}
          unitType="$"
          currency={currency}
        />

        {/* occupancy rate box */}
        <DataBoxComponent
          kpi="occupancy"
          selectedMainKpi={selectedMainKpi}
          kpiName="Occupancy Rate"
          value={marketLevelOccupancyRate}
          yoYChange={occupancyYoYChange}
          isLoading={areKPIsLoading}
          unitType="%"
          currency={currency}
        />

        {/* daily rate box */}
        <DataBoxComponent
          kpi="daily"
          selectedMainKpi={selectedMainKpi}
          kpiName="Nightly Rate"
          value={marketLevelDailyRate}
          yoYChange={dailyYoYChange}
          isLoading={areKPIsLoading}
          unitType="$"
          currency={currency}
        />

        {/* no of listings box */}
        <DataBoxComponent
          kpi="listing"
          selectedMainKpi={selectedMainKpi}
          kpiName="Active Listings"
          value={marketLevelActiveListings}
          yoYChange={listingYoYChange}
          isLoading={areKPIsLoading}
          unitType=""
          currency={currency}
        />

        {/* Seasonaity box */}
        <DataBoxComponent
          kpi="seasonality"
          selectedMainKpi={selectedMainKpi}
          kpiName="Seasonality"
          value={seasonalityData['seasonality']}
          yoYChange={''}
          isLoading={areKPIsLoading}
          unitType=""
          currency={currency}
        />

        {/* Trends box */}
        <DataBoxComponent
          kpi="trend"
          selectedMainKpi={selectedMainKpi}
          kpiName="Trend"
          value={lastYearMarketTrend || ''}
          yoYChange={''}
          isLoading={areKPIsLoading}
          unitType="%"
          currency={currency}
        />
      </Box>

      {selectedMainKpi == 'listing' && (
        <Box sx={{ px: '10px', width: '100%', my: '10px' }}>
          {ComponentFilterSupportComingSoonText}
        </Box>
      )}

      {/* Graph Box */}
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'center',
          pr: '20px',
        }}
      >
        <IconButton
          color="primary"
          onClick={handleDownloadGraph}
          sx={{ height: '18px', width: '18px' }}
        >
          <DownloadRoundedIcon />
        </IconButton>
      </Box>

      <Box
        sx={{
          height: matches ? '280px' : '300px',
          width: '100%',
          pointerEvents: isAccessDenied ? 'none' : 'auto',
          paddingTop: '5px',
          ...(matches ? { marginBottom: '10px', marginTop: '15px' } : {}),
        }}
        ref={mainKpiRevenueChartRef}
      >
        {!isGraphDataPresent && selectedMainKpi === 'seasonality' && (
          <Box sx={{ height: '100%', width: '100%' }}>
            {areKPIsLoading ? (
              <KPIDataLoading />
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                {SeasonalityChartMemoed}
              </ResponsiveContainer>
            )}
          </Box>
        )}
        {!isGraphDataPresent && selectedMainKpi === 'trend' && (
          <Box sx={{ height: '100%', width: '100%' }}>
            {areKPIsLoading ? (
              <KPIDataLoading />
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                {TrendsChartMemo}
              </ResponsiveContainer>
            )}
          </Box>
        )}
        {isGraphDataPresent && !areKPIsLoading ? (
          <ResponsiveContainer
            key={`${selectedMainKpi}-${timePeriod}-${selectedPercentile}`}
            width="100%"
            height="100%"
            id="main-kpi-graph-container"
          >
            {selectedMainKpi === 'occupancy' && occupancyChartDataWithBand ? (
              <ComposedChart
                data={occupancyChartDataWithBand}
                margin={{ top: 0, right: 30, bottom: 20, left: 30 }}
              >
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis
                dataKey="month"
                style={{ fontFamily: 'Roboto', fontSize: '12px' }}
                tickLine={false}
                tickFormatter={monthTickFormattor}
                ticks={
                  matches
                    ? (() => {
                        const data = isAccessDenied
                          ? GRAPH_DUMMY_DATA_MAPPING[selectedMainKpi][timePeriod]
                          : GRAPH_DATA_MAPPING[selectedMainKpi][timePeriod];
                        if (!data || data.length === 0) return [];
                        const first = data[0]?.month;
                        const middle = data[Math.floor(data.length / 2)]?.month;
                        const last = data[data.length - 1]?.month;
                        return [first, middle, last].filter(Boolean);
                      })()
                    : undefined
                }
              />
              <YAxis
                axisLine={false}
                tickLine={false}
                style={{ fontFamily: 'Roboto', fontSize: '12px' }}
                tickFormatter={combineFormatterYAxis}
                domain={selectedMainKpi.includes('occupancy') ? [0, 100] : undefined}
              />
              <Tooltip
                content={<CustomTooltip isHoveringLabel={isHoveringLabel} />}
                position={{ y: -150 }}
              />
              <Legend
                verticalAlign="bottom"
                content={<CustomLegend />}
                wrapperStyle={{ bottom: 0 }}
              />
              <Area
                type="monotone"
                dataKey="occupancy_confirmed_low"
                stroke="none"
                fill="transparent"
                stackId="occupancyBand"
                isAnimationActive={true}
                animationDuration={150}
              />
              <Area
                type="monotone"
                dataKey="occupancy_band"
                stroke="none"
                fill={COLOR_PRIMARY_2}
                fillOpacity={0.35}
                stackId="occupancyBand"
                isAnimationActive={true}
                animationDuration={150}
              />
              <Line
                type="monotone"
                dataKey={`LTMData.${GRAPH_LINE_DATA_MAPPING[selectedMainKpi][selectedPercentile]}`}
                stroke={COLOR_PRIMARY_2}
                strokeWidth={3}
              />
              {timePeriod == 24 && (
                <Line
                  type="monotone"
                  dataKey={`last2YearsData.${GRAPH_LINE_DATA_MAPPING[selectedMainKpi][selectedPercentile]}`}
                  stroke="#4FA2B1"
                  strokeDasharray="5 5"
                />
              )}
              {timePeriod == 36 && (
                <Line
                  type="monotone"
                  dataKey={`last2YearsData.${GRAPH_LINE_DATA_MAPPING[selectedMainKpi][selectedPercentile]}`}
                  stroke={COLOR_PRIMARY_1_800}
                  strokeDasharray="5 5"
                />
              )}
              {timePeriod == 36 && (
                <Line
                  type="monotone"
                  dataKey={`last3YearsData.${GRAPH_LINE_DATA_MAPPING[selectedMainKpi][selectedPercentile]}`}
                  stroke={COLOR_PRIMARY_2_300}
                  strokeDasharray="5 5"
                />
              )}
              {/* Add reference line for Aug 2023 if it exists */}
              {showDataCoverageUpdateSignalReferenceLine && (
                <ReferenceLine
                  x={'Aug'}
                  stroke={COLOR_GREY_400}
                  strokeDasharray="4 4"
                  strokeWidth={2}
                  label={
                    <CustomReferenceLabel
                      setIsHoveringLabel={setIsHoveringLabel}
                      isMobileScreen={matches}
                    />
                  }
                />
              )}
            </ComposedChart>
            ) : (
              <LineChart
                data={
                  isAccessDenied
                    ? GRAPH_DUMMY_DATA_MAPPING[selectedMainKpi][timePeriod]
                    : GRAPH_DATA_MAPPING[selectedMainKpi][timePeriod]
                }
                margin={{ top: 0, right: 30, bottom: 20, left: 30 }}
              >
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis
                dataKey="month"
                style={{ fontFamily: 'Roboto', fontSize: '12px' }}
                tickLine={false}
                tickFormatter={monthTickFormattor}
                ticks={
                  matches
                    ? (() => {
                        const data = isAccessDenied
                          ? GRAPH_DUMMY_DATA_MAPPING[selectedMainKpi][timePeriod]
                          : GRAPH_DATA_MAPPING[selectedMainKpi][timePeriod];
                        if (!data || data.length === 0) return [];
                        const first = data[0]?.month;
                        const middle = data[Math.floor(data.length / 2)]?.month;
                        const last = data[data.length - 1]?.month;
                        return [first, middle, last].filter(Boolean);
                      })()
                    : undefined
                }
              />
              <YAxis
                axisLine={false}
                tickLine={false}
                style={{ fontFamily: 'Roboto', fontSize: '12px' }}
                tickFormatter={combineFormatterYAxis}
                domain={selectedMainKpi.includes('occupancy') ? [0, 100] : undefined}
              />
              <Tooltip
                content={<CustomTooltip isHoveringLabel={isHoveringLabel} />}
                position={{ y: -150 }}
              />
              <Legend
                verticalAlign="bottom"
                content={<CustomLegend />}
                wrapperStyle={{ bottom: 0 }}
              />
              <Line
                type="monotone"
                dataKey={`LTMData.${GRAPH_LINE_DATA_MAPPING[selectedMainKpi][selectedPercentile]}`}
                stroke={COLOR_PRIMARY_2}
                strokeWidth={3}
              />
              {timePeriod == 24 && (
                <Line
                  type="monotone"
                  dataKey={`last2YearsData.${GRAPH_LINE_DATA_MAPPING[selectedMainKpi][selectedPercentile]}`}
                  stroke="#4FA2B1"
                  strokeDasharray="5 5"
                />
              )}
              {timePeriod == 36 && (
                <Line
                  type="monotone"
                  dataKey={`last2YearsData.${GRAPH_LINE_DATA_MAPPING[selectedMainKpi][selectedPercentile]}`}
                  stroke={COLOR_PRIMARY_1_800}
                  strokeDasharray="5 5"
                />
              )}
              {timePeriod == 36 && (
                <Line
                  type="monotone"
                  dataKey={`last3YearsData.${GRAPH_LINE_DATA_MAPPING[selectedMainKpi][selectedPercentile]}`}
                  stroke={COLOR_PRIMARY_2_300}
                  strokeDasharray="5 5"
                />
              )}
              {showDataCoverageUpdateSignalReferenceLine && (
                <ReferenceLine
                  x={'Aug'}
                  stroke={COLOR_GREY_400}
                  strokeDasharray="4 4"
                  strokeWidth={2}
                  label={
                    <CustomReferenceLabel
                      setIsHoveringLabel={setIsHoveringLabel}
                      isMobileScreen={matches}
                    />
                  }
                />
              )}
            </LineChart>
            )}
          </ResponsiveContainer>
        ) : isTrendOrSeasonality ? (
          <></>
        ) : (
          <LoadingCard height={150} type="bars" />
        )}
        {isAccessDenied && (
          <div
            style={{
              height: isTrendOrSeasonality ? (matches ? '70%' : '76%') : '76%',
              position: 'relative',
              top: isTrendOrSeasonality ? (matches ? '-111%' : '-103%') : '-103%',
              left: isTrendOrSeasonality ? '40px' : '84px',
              width: isTrendOrSeasonality ? '96%' : '90%',
              background: '#ffffff',
              backdropFilter: 'blur(6px)',
              opacity: 0.8,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          ></div>
        )}
        {isAccessDenied && (
          <Typography
            variant="body1"
            sx={{
              position: 'relative',
              top: isTrendOrSeasonality ? (matches ? '-165%' : '-150%') : '-150%',
              // left: upgradeMessage == EXPERT_CTA_FOR_TAILOR_REGION ? '30%' : '40%',
              left: isTrendOrSeasonality ? (matches ? '30%' : '40%') : '40%',
              fontSize: '20px',
              fontWeight: 500,
              color: COLOR_PRIMARY_2,
              cursor: 'pointer',
              pointerEvents: 'auto',
              textDecoration: 'underline',
            }}
            onClick={() => {
              handleOnSampleClick(forcedPlan);
            }}
          >
            {upgradeMessage}
          </Typography>
        )}
      </Box>

      {/* {isGraphDataPresent && ( */}
      <Box
        sx={{
          height: '10%',
          width: '100%',
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'center',
        }}
      >
        {/* timePeriod dropdown */}
        {isGraphDataPresent && (
          <Box
            sx={{
              height: '100%',
            }}
          >
            <FormControl variant="standard" size="small" sx={{ width: 150, padding: '0px 5px' }}>
              <Select
                labelId="time-period-select"
                id="time-period-select"
                value={`${timePeriod}`}
                onChange={handleTimePeriodChange}
                label="Time Period"
              >
                <MenuItem value={24}>Last 2 years</MenuItem>
                <MenuItem value={36}>Last 3 years</MenuItem>
              </Select>
            </FormControl>
          </Box>
        )}

        {/* Percentile dropdown */}
        {selectedMainKpi !== 'listing' && (
          <Box
            sx={{
              height: '100%',
            }}
          >
            <FormControl variant="standard" size="small" sx={{ width: 150, padding: '0px 20px' }}>
              <Select
                labelId="percentile-select"
                id="percentile-select"
                value={selectedPercentile ? `${selectedPercentile}` : ''}
                onChange={event => {
                  isAccessDenied
                    ? handleOnSampleClick('expert')
                    : setSelectedpercentile(event.target.value);
                  logAmplitudeEvent('main_kpi_metric_configured');
                }}
                renderValue={selected => {
                  const displayMapping = {
                    '90': 'Top 10%',
                    '75': 'Top 25%',
                    '50': 'Median',
                    '25': 'Top 75%',
                  };
                  return displayMapping[selected] || '';
                }}
              >
                <MenuItem value="90">
                  Top 10%{' '}
                  {isAccessDenied && (
                    <LockSharpIcon style={{ fontSize: 18, color: 'inherit', marginLeft: '5px' }} />
                  )}
                </MenuItem>
                <MenuItem value="75">
                  Top 25%{' '}
                  {isAccessDenied && (
                    <LockSharpIcon style={{ fontSize: 18, color: 'inherit', marginLeft: '5px' }} />
                  )}
                </MenuItem>
                <MenuItem value="50">
                  Median <LockSharpIcon style={{ display: 'none' }} />
                </MenuItem>
                <MenuItem value="25">
                  Top 75%{' '}
                  {isAccessDenied && (
                    <LockSharpIcon style={{ fontSize: 18, color: 'inherit', marginLeft: '5px' }} />
                  )}
                </MenuItem>
              </Select>
            </FormControl>
          </Box>
        )}

        {selectedMainKpi === 'listing' && (
          <Box
            sx={{
              height: '100%',
              width: '200px',
            }}
          >
            <FormControl variant="standard" size="small" sx={{ width: 150, padding: '0px 20px' }}>
              <Select
                labelId="percentile-select"
                id="percentile-select"
                value={'50'}
                onChange={event => {
                  setSelectedpercentile(event.target.value);
                }}
                label="Time Period"
              >
                <MenuItem value={50}>Median</MenuItem>
              </Select>
            </FormControl>
          </Box>
        )}
      </Box>
      {/* )} */}
    </Box>
  );
};

export default MainKPICard;
